import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { VendorService } from '../../services/vendor.service';

@Component({
	selector: 'app-move-vendor-transaction',
	templateUrl: './move-vendor-transaction.component.html',
	styleUrls: ['./move-vendor-transaction.component.scss']
})
export class MoveVendorTransactionComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	private transactionsSub: any = null;
	private vendorSearchSub: any = null;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	color: string;
	fromvendor = new FormControl('');
	tovendor = new FormControl('');
	filterControl = new FormControl('');
	typeFilterControl = new FormControl<number[]>([]);

	sending = false;
	loading = false;
	moving = false;
	fromresults: any[] = [];
	toresults: any[] = [];
	from: any = null;
	to: any = null;
	transactions: any = null;
	dataSource = new MatTableDataSource<any>([]);
	selected: any[] = [];

	displayedColumns: string[] = [
		'select',
		'transno',
		'typename',
		'trandate',
		'duedate',
		'suppreference',
		'userid',
		'settled',
		'totalamount',
		'allocated',
		'balance'
	];

	typeFilters = [
		{ value: 20, name: 'Invoice' },
		{ value: 21, name: 'Credit' },
		{ value: 22, name: 'Payment' }
	];

	constructor(
		private location: Location,
		public router: Router,
		private globalSearchService: GlobalSearchService,
		public vendorService: VendorService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.dataSource.filterPredicate = (row: any, filter: string) => {
			const text = filter.toLowerCase();
			const parts = [
				row.transno,
				row.typename,
				row.suppreference,
				row.userid,
				row.settled,
				row.totalamount,
				row.allocated,
				row.balance,
				row.trandate,
				row.duedate
			].filter(Boolean).map((x) => String(x).toLowerCase());
			return parts.some((p) => p.indexOf(text) !== -1);
		};

		this.filterControl.valueChanges
			.pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe((value: string) => this.applyFilter(value || ''));

		this.typeFilterControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.applyTypeFilter());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.unsubscribeTransactions();
		if (this.vendorSearchSub) {
			this.vendorSearchSub.unsubscribe();
			this.vendorSearchSub = null;
		}
	}

	private unsubscribeTransactions(): void {
		if (this.transactionsSub) {
			this.transactionsSub.unsubscribe();
			this.transactionsSub = null;
		}
	}

	back(): void {
		this.location.back();
	}

	setTo(value: any): void {
		this.to = value;
	}

	setFrom(value: any): void {
		this.from = value;
		this.resetFilters();
		this.loadTransactions();
	}

	private resetFilters(): void {
		this.selected = [];
		this.filterControl.setValue('', { emitEvent: false });
		this.typeFilterControl.setValue([], { emitEvent: false });
		this.dataSource.filter = '';
	}

	private loadTransactions(): void {
		if (!this.from) return;
		this.loading = true;
		this.transactions = null;
		this.dataSource.data = [];

		this.unsubscribeTransactions();
		this.transactionsSub = this.vendorService
			.getSupplierTransactions(this.from.supplierid)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(r: any) => {
					this.loading = false;
					this.transactions = r;
					const list = (r && r.transactions) ? r.transactions : [];
					this.dataSource.data = list;
					this.dataSource.sort = this.sort;
					this.dataSource.paginator = this.paginator;
					this.applyTypeFilter();
				},
				() => {
					this.loading = false;
					this.transactions = { transactions: [] };
					this.globalSearchService.showNotification('Unable to load transactions', 'danger', 'bottom', 'right');
				}
			);
	}

	get totalCount(): number {
		return this.transactions && Array.isArray(this.transactions.transactions)
			? this.transactions.transactions.length
			: 0;
	}

	toggleType(value: number): void {
		const current = Array.isArray(this.typeFilterControl.value) ? [...this.typeFilterControl.value] : [];
		const idx = current.indexOf(value);
		if (idx >= 0) {
			current.splice(idx, 1);
		} else {
			current.push(value);
		}
		this.typeFilterControl.setValue(current);
	}

	isTypeActive(value: number): boolean {
		const current = this.typeFilterControl.value;
		return Array.isArray(current) && current.indexOf(value) !== -1;
	}

	applyFilter(value: string): void {
		this.dataSource.filter = value.trim().toLowerCase();
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	applyTypeFilter(): void {
		const selected = this.typeFilterControl.value;
		if (!this.transactions || !Array.isArray(this.transactions.transactions)) {
			return;
		}
		if (!selected || selected.length === 0) {
			this.dataSource.data = [...this.transactions.transactions];
		} else {
			const set = new Set(selected);
			this.dataSource.data = this.transactions.transactions.filter((t: any) =>
				set.has(Number(t.type))
			);
		}
		this.dataSource._updateChangeSubscription();
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	isSelected(t: any): boolean {
		return this.selected.some((s) => s === t || (s.type === t.type && s.transno === t.transno && s.id === t.id));
	}

	toggleSelection(t: any): void {
		const idx = this.selected.findIndex(
			(s) => s === t || (s.type === t.type && s.transno === t.transno && s.id === t.id)
		);
		if (idx >= 0) {
			this.selected.splice(idx, 1);
		} else {
			this.selected.push(t);
		}
	}

	checkAll(event: any): void {
		const list = this.dataSource.filteredData;
		if (event.target.checked) {
			const existing = new Set(this.selected.map((s) => `${s.type}-${s.transno}-${s.id}`));
			list.forEach((t) => {
				const key = `${t.type}-${t.transno}-${t.id}`;
				if (!existing.has(key)) {
					this.selected.push(t);
					existing.add(key);
				}
			});
		} else {
			const toRemove = new Set(list.map((t) => `${t.type}-${t.transno}-${t.id}`));
			this.selected = this.selected.filter((s) => !toRemove.has(`${s.type}-${s.transno}-${s.id}`));
		}
	}

	isAllChecked(): boolean {
		const list = this.dataSource.filteredData;
		if (!list.length) return false;
		return list.every((t) => this.isSelected(t));
	}

	moveTransactions(): void {
		if (this.selected.length === 0 || this.moving) return;
		const count = this.selected.length;
		if (!confirm(`Move ${count} transaction(s) to ${this.to.supplierid} ${this.to.suppname || ''}? Allocations are NOT moved.`)) {
			return;
		}
		const data = {
			from: this.from,
			to: this.to,
			transactions: this.selected
		};
		this.moving = true;
		this.vendorService.moveSupplierTransactions(data).subscribe(
			(r: any) => {
				this.moving = false;
				if (r && r.success) {
					this.globalSearchService.showNotification(
						`${r.moved != null ? r.moved : count} transaction(s) moved to ${this.to.supplierid}`,
						'success',
						'bottom',
						'right'
					);
					this.resetFilters();
					this.loadTransactions();
				} else {
					this.globalSearchService.showNotification(
						(r && r.message) ? r.message : 'Unable to move transactions',
						'danger',
						'bottom',
						'right'
					);
				}
			},
			() => {
				this.moving = false;
				this.globalSearchService.showNotification('Unable to move transactions', 'danger', 'bottom', 'right');
			}
		);
	}

	vendorSearch(keyword: string, type: 'from' | 'to'): void {
		if (this.vendorSearchSub) {
			this.vendorSearchSub.unsubscribe();
		}
		this.sending = true;
		this.vendorSearchSub = this.vendorService
			.vendorSearch({ keywords: keyword })
			.pipe(takeUntil(this.destroy$))
			.subscribe((results: any) => {
				this.sending = false;
				const list = Array.isArray(results) ? results : (results && Array.isArray(results.data) ? results.data : []);
				if (type === 'from') {
					this.fromresults = list;
				} else {
					this.toresults = list;
				}
			});
	}

	typeClass(t: any): string {
		switch (Number(t && t.type)) {
			case 20: return 'pill--invoice';
			case 21: return 'pill--credit';
			case 22: return 'pill--payment';
			default: return 'pill--other';
		}
	}

	displayFn(vendor: any): string {
		return vendor ? `${vendor.supplierid} ${vendor.suppname || ''}`.trim() : '';
	}
}
